/**
 * File System Operations
 * Hybrid approach: File System Access API + Download fallback
 */

class FileSystemManager {
  constructor() {
    this.directoryHandle = null;
    this.hasPermission = false;
    this.supportsFileSystemAccess = 'showDirectoryPicker' in window;
  }

  /**
   * Request directory access from user
   */
  async requestDirectoryAccess() {
    if (!this.supportsFileSystemAccess) {
      console.warn('File System Access API not supported, using download fallback');
      return false;
    }

    try {
      this.directoryHandle = await window.showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'documents'
      });

      // Verify we have write permission
      const permission = await this.directoryHandle.requestPermission({ mode: 'readwrite' });
      this.hasPermission = permission === 'granted';

      if (this.hasPermission) {
        console.log('[FileSystem] Directory access granted:', this.directoryHandle.name);
      }

      return this.hasPermission;
    } catch (error) {
      console.error('[FileSystem] Directory access denied:', error);
      return false;
    }
  }

  /**
   * Read a file
   */
  async readFile(path) {
    if (!this.hasPermission) {
      throw new Error('No directory access. Call requestDirectoryAccess() first.');
    }

    try {
      const fileHandle = await this.getFileHandle(path);
      const file = await fileHandle.getFile();
      const content = await file.text();

      return {
        success: true,
        content,
        path,
        size: file.size,
        modified: file.lastModified
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        path
      };
    }
  }

  /**
   * Write a file (create or update)
   */
  async writeFile(path, content) {
    // Validate inputs
    if (!path) {
      console.error('[FileSystem] Write error: path is required');
      return {
        success: false,
        error: 'Path is required'
      };
    }

    if (content === undefined || content === null) {
      content = ''; // Allow empty files
    }

    if (!this.hasPermission) {
      // Fallback: download file
      return this.downloadFile(path, content);
    }

    try {
      const fileHandle = await this.getFileHandle(path, true);
      const writable = await fileHandle.createWritable();
      await writable.write(content);
      await writable.close();

      return {
        success: true,
        path,
        size: content.length,
        action: 'written'
      };
    } catch (error) {
      console.error('[FileSystem] Write error:', error);
      // Fallback to download
      return this.downloadFile(path, content);
    }
  }

  /**
   * List directory contents
   */
  async listDirectory(path = '.') {
    if (!this.hasPermission) {
      throw new Error('No directory access. Call requestDirectoryAccess() first.');
    }

    try {
      const dirHandle = path === '.' || path === '/'
        ? this.directoryHandle
        : await this.getDirectoryHandle(path);

      const files = [];

      for await (const entry of dirHandle.values()) {
        files.push({
          name: entry.name,
          type: entry.kind, // 'file' or 'directory'
          path: path === '.' ? entry.name : `${path}/${entry.name}`
        });
      }

      return {
        success: true,
        files,
        path
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        path
      };
    }
  }

  /**
   * Create directory
   */
  async createDirectory(path) {
    if (!this.hasPermission) {
      throw new Error('No directory access. Call requestDirectoryAccess() first.');
    }

    try {
      await this.getDirectoryHandle(path, true);
      return {
        success: true,
        path
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        path
      };
    }
  }

  /**
   * Delete file or directory
   */
  async delete(path) {
    if (!this.hasPermission) {
      throw new Error('No directory access. Call requestDirectoryAccess() first.');
    }

    try {
      const parts = path.split('/');
      const name = parts.pop();
      const parentPath = parts.join('/');

      const parentHandle = parentPath
        ? await this.getDirectoryHandle(parentPath)
        : this.directoryHandle;

      await parentHandle.removeEntry(name, { recursive: true });

      return {
        success: true,
        path
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        path
      };
    }
  }

  /**
   * Get file handle (navigates nested paths)
   */
  async getFileHandle(path, create = false) {
    const parts = path.split('/').filter(p => p);
    const fileName = parts.pop();

    let currentHandle = this.directoryHandle;

    // Navigate to parent directory
    for (const part of parts) {
      currentHandle = await currentHandle.getDirectoryHandle(part, { create });
    }

    return await currentHandle.getFileHandle(fileName, { create });
  }

  /**
   * Get directory handle (navigates nested paths)
   */
  async getDirectoryHandle(path, create = false) {
    if (!path || path === '.' || path === '/') {
      return this.directoryHandle;
    }

    const parts = path.split('/').filter(p => p);
    let currentHandle = this.directoryHandle;

    for (const part of parts) {
      currentHandle = await currentHandle.getDirectoryHandle(part, { create });
    }

    return currentHandle;
  }

  /**
   * Download file as fallback
   */
  downloadFile(path, content) {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = path.split('/').pop();
    a.click();
    URL.revokeObjectURL(url);

    return {
      success: true,
      path,
      size: content.length,
      action: 'downloaded'
    };
  }

  /**
   * Download multiple files as ZIP
   */
  async downloadAsZip(files, zipName = 'project.zip') {
    try {
      // Using JSZip (need to add to package.json)
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      // Add files to zip
      for (const [path, content] of Object.entries(files)) {
        zip.file(path, content);
      }

      // Generate ZIP
      const blob = await zip.generateAsync({ type: 'blob' });

      // Download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = zipName;
      a.click();
      URL.revokeObjectURL(url);

      return {
        success: true,
        filesCount: Object.keys(files).length,
        action: 'downloaded-zip'
      };
    } catch (error) {
      console.error('[FileSystem] ZIP error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Check if we have access
   */
  hasAccess() {
    return this.hasPermission;
  }

  /**
   * Get current directory name
   */
  getCurrentDirectory() {
    return this.directoryHandle?.name || null;
  }
}

// Singleton instance
let fileSystemManager = null;

export function getFileSystemManager() {
  if (!fileSystemManager) {
    fileSystemManager = new FileSystemManager();
  }
  return fileSystemManager;
}

export default FileSystemManager;
